<script setup lang="ts">
import type { Blocks, BreadcrumbListItem, Image, Metadata } from 'types/index';

type newsPage = NewsDetailPageContent & {
  article: string;
  heroImage: Image;
  id: string;
};

const props = defineProps<{
  blocks: Blocks;
  content: newsPage;
  breadcrumbs: BreadcrumbListItem[];
  metadata: Metadata;
}>();

const computedBlocks = computed(() => {
  return props.blocks.blocks || {};
});

const formattedDate = computed(() => {
  return new Date(props.content.date).toLocaleDateString(props.metadata.culture, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
});

const videoData = reactive<VideoData>({
  video: props.content.video,
  id: '',
  platform: '',
  autoplay: false,
  image: props.content.heroImage,
});

function setVideoData() {
  videoData.video = props.content.video;
  videoData.id = props.content.id;
  videoData.platform = props.content.platform;
  videoData.autoplay = props.content.autoplay;
  videoData.image = props.content.heroImage;
  return videoData;
}
</script>

<template>
  <section>
    <section class="bg-gray-20">
      <div class="container flex flex-col justify-center gap-8 py-20">
        <h1
          v-if="content?.title"
          class="text-h1 font-bold"
        >
          {{ content.title }}
        </h1>

        <p
          v-if="content?.shortDescription"
          class="max-w-[90ch]"
        >
          {{ content.shortDescription }}
        </p>

        <p v-if="content?.date">
          {{ formattedDate }}
        </p>

        <figure
          v-if="content?.heroImage || content?.video || content?.id"
          class="bg-gray-40"
        >
          <BaseImage
            v-if="content?.heroImage && !content?.id && !content?.video"
            class="h-[50vh] w-full object-cover"
            :height="600"
            :width="1000"
            :image="content.heroImage"
          />

          <BaseVideo
            v-if="content?.video || content?.id"
            :video-data="setVideoData()"
          />
        </figure>
      </div>
    </section>

    <div class="narrow-container py-20">
      <BaseRte
        v-if="content?.article"
        :content="content?.article"
      />
    </div>

    <BlockList
      v-if="computedBlocks"
      :blocks="computedBlocks"
    />
  </section>
</template>
